import { useLocalSearchParams, useRouter } from 'expo-router'
import { useMemo, useState } from 'react'
import { type LayoutChangeEvent, ScrollView, StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { LIVE_STATUS_SORT_ORDER, type LiveStatus } from '@/constants/monitor-status'
import type { LocationCircle } from '@/hooks/useActiveLocations'
import { useMonitorCardsView } from '@/stores/monitor-store'
import type { UserLocationWithStatus } from '@/types/movement'
import { UserCard } from '../cards/UserCard'

interface Props {
    userLocations: UserLocationWithStatus[]
    locationCircles: LocationCircle[]
    isPending: boolean
}

type StatusFilter = LiveStatus | 'working' | 'off' | 'late' | null

function matchesFilter(
    userLocations: UserLocationWithStatus[],
    filter: StatusFilter
): UserLocationWithStatus[] {
    if (!filter) return userLocations
    if (filter === 'working') {
        return userLocations.filter(u => {
            const s = u.trackingStatus.status
            return (
                s === 'working_moving' ||
                s === 'working_on_site' ||
                s === 'working_idle' ||
                s === 'working_untracked'
            )
        })
    }
    if (filter === 'late') {
        return userLocations.filter(u => {
            const s = u.trackingStatus.status
            return s === 'after_hours_working' || s === 'before_hours_working'
        })
    }
    if (filter === 'off') {
        return userLocations.filter(u => {
            const s = u.trackingStatus.status
            return s === 'off_hours' || s === 'not_tracking' || s === 'no_data_today'
        })
    }
    return userLocations.filter(u => u.trackingStatus.status === filter)
}

function sortUsers(userLocations: UserLocationWithStatus[]): UserLocationWithStatus[] {
    const copy = [...userLocations]
    return copy.sort((a, b) => {
        const byStatus =
            LIVE_STATUS_SORT_ORDER[a.trackingStatus.status] -
            LIVE_STATUS_SORT_ORDER[b.trackingStatus.status]
        if (byStatus !== 0) return byStatus
        return a.userName.localeCompare(b.userName)
    })
}

export function CardsView({ userLocations, locationCircles, isPending }: Props) {
    const theme = useTheme()
    const [containerWidth, setContainerWidth] = useState(0)
    const { statusFilter } = useMonitorCardsView()
    const router = useRouter()
    const { orgSlug } = useLocalSearchParams<{ orgSlug: string }>()

    const handleLayout = (e: LayoutChangeEvent) => {
        setContainerWidth(e.nativeEvent.layout.width)
    }

    const GRID_MAX_WIDTH = 900
    const gridWidth = containerWidth > 0 ? Math.min(containerWidth - 32, GRID_MAX_WIDTH) : 0
    const columnCount = gridWidth >= 620 ? 2 : 1
    const cardMinWidth =
        gridWidth > 0 ? Math.floor((gridWidth - (columnCount - 1) * 12) / columnCount) : 0

    const displayed = useMemo(() => {
        // Users with geo tracking disabled can never report location; exclude
        // them from the roster entirely so they don't clutter the view.
        const trackable = userLocations.filter(u => u.trackingStatus.status !== 'not_tracking')
        const filtered = matchesFilter(trackable, statusFilter)
        return sortUsers(filtered)
    }, [userLocations, statusFilter])

    const handleCardPress = (userId: string) => {
        router.push(`/${orgSlug}/monitor/${userId}` as `/${string}`)
    }

    return (
        <View style={styles.flex} onLayout={handleLayout}>
            <ScrollView
                style={{ backgroundColor: theme.colors.background }}
                contentContainerStyle={styles.scroll}
            >
                {displayed.length === 0 ? (
                    <View style={styles.empty}>
                        <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                            {isPending ? 'Loading…' : 'No team members to show'}
                        </Text>
                    </View>
                ) : gridWidth > 0 ? (
                    <View style={[styles.grid, { width: gridWidth }]}>
                        {displayed.map(user => (
                            <View
                                key={user.userId}
                                style={[styles.cardWrap, { width: cardMinWidth }]}
                            >
                                <UserCard
                                    user={user}
                                    locationCircles={locationCircles}
                                    onPress={handleCardPress}
                                />
                            </View>
                        ))}
                    </View>
                ) : null}
            </ScrollView>
        </View>
    )
}

const styles = StyleSheet.create({
    flex: {
        flex: 1,
    },
    scroll: {
        padding: 16,
        alignItems: 'center',
    },
    grid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: 12,
        justifyContent: 'center',
    },
    cardWrap: {
        minWidth: 260,
        alignSelf: 'stretch',
    },
    empty: {
        padding: 32,
        alignItems: 'center',
    },
})
